import { K8SResourceType, KV, KVString, OperateFunction } from "yam-types"
import { Settings } from "../settings"
import { CertManagerCertificate } from "../types"

const CERT_MANAGER_API_GROUP = "cert-manager.io"
const CERT_MANAGER_API_VERSION = "v1"

const certManagerOperator: OperateFunction<CertManagerCertificate> = async (plan, diff, settings: Settings) => {
  if (!settings.issuer) {
    plan.log.error(`no issuer specified in cert-manager plugin settings, all actions skipped !`)
    return
  }
  if (!diff.hasDiff) {
    return
  }
  if (diff.deletedItems.length > 0) {
    const deletedCerts = diff.deletedItems.map(x => x.name).join(',')
    if (settings.retainCertsOnDelete) {
      plan.log.warn(`deleted certificates detected, but not being deleted due to 'retainCertsOnDelete' setting, plz manually delete them: ${deletedCerts}`)
    } else {
      plan.log.info(`'cert' part has been deleted, will delete TLS: ${deletedCerts}`)
      plan.action(async (ctx) => {
        for (const cert of diff.deletedItems) {
          await ctx.k8sClient.delete({
            kind: K8SResourceType.ClusterRole,
            name: cert.name,
          })
        }
      }, "delete-certificates:" + deletedCerts)
    }
  }

  // sync certificates
  if (diff.newItems.length > 0 || diff.modifiedItems.length > 0) {
    const certs = [...diff.newItems, ...diff.modifiedItems.map(x => x.current)]
    plan.action(async (ctx) => {
      // check  Certificate CRD
      if (!ctx.k8sClient.crdExists(K8SResourceType.Certificate, CERT_MANAGER_API_GROUP)) {
        ctx.log.error(`Cert Manager is not installed on cluster, action skipped. To install Cert Manager, please refer: https://cert-manager.io/docs/`)
        return
      }

      const issuerKind = settings.issuer?.isClusterIssuer ? K8SResourceType.ClusterIssuer : K8SResourceType.Issuer
      const issuerName = settings.issuer?.name

      // create issuer if needed
      if (settings.issuer?.createIssuer) {
        await ctx.k8sClient.apply({
          kind: issuerKind,
          apiVersion: CERT_MANAGER_API_VERSION,
          metadata: {
            name: settings.issuer.name,
            namespace: settings.issuer.isClusterIssuer ? undefined : plan.data.namespace,
            annotations: settings.issuer.issuerTemplate.annotations as KVString,
            labels: settings.issuer.issuerTemplate.labels as KVString,
          },
          spec: settings.issuer.issuerTemplate.spec as KV
        })
        ctx.log.info(`${issuerKind} ${settings.issuer.name} has been created/updated.`)
      }

      for (const cert of certs) {
        await ctx.k8sClient.apply(Object.assign({
          kind: K8SResourceType.Certificate,
          apiVersion: CERT_MANAGER_API_VERSION,
          metadata: {
            name: cert.name,
            namespace: plan.data.namespace
          },
          spec: {
            dnsNames: cert.domains,
            issuerRef: {
              kind: issuerKind,
              name: issuerName,
              secretName: cert.name
            }
          }
        }, cert.certificateTemplate))
        ctx.log.info(`Certificate '${cert.name}' has been created in namespace '${plan.data.namespace}'`)
      }
    }, `sync-certificates:${certs.map(c => c.name).join(',')}`)
  } else {
    plan.log.info(`no changes at certificate part, operations skipped.`)
  }
}

export default certManagerOperator

